// Messages — every user's own inbox of admin broadcast messages (all roles,
// distributors included). /messages lists them newest first; /messages/:id
// shows one in full with its attachments and an "OK, I have read it" button
// while it is still unread. The bell's broadcast notifications link here.
// Backend: routes_broadcasts.py (/broadcasts/inbox, /broadcasts/{id}, /ack).
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, ChevronRight, Inbox, Loader2, Mail, Paperclip, RefreshCw, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  BroadcastAttachments, LinkifiedText, fmtWhen, fmtYmd, BROADCASTS_CHANGED_EVENT,
} from "@/components/BroadcastPopup";

const PAGE_SIZE = 20;

function UrgentBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 whitespace-nowrap">
      <AlertTriangle className="h-3 w-3" /> Urgent
    </span>
  );
}

function StateLabel({ m }) {
  if (m?.is_active === false) {
    return (
      <span className="inline-flex items-center rounded-md border border-neutral-200 bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-600 whitespace-nowrap">
        Withdrawn
      </span>
    );
  }
  if (m?.is_expired) {
    return (
      <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 whitespace-nowrap">
        Expired
      </span>
    );
  }
  return null;
}

// Withdrawn messages no longer ask to be read (same rule as the server's
// unread count), so they never stay bold.
const isUnread = (m) => !m.read_at && m.is_active !== false;

function MessageList({ wrap }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get("/broadcasts/inbox", { params: { page: 1, page_size: PAGE_SIZE } });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
      setPage(1);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // the popup / detail page just marked something read — refresh bold rows
    window.addEventListener(BROADCASTS_CHANGED_EVENT, load);
    return () => window.removeEventListener(BROADCASTS_CHANGED_EVENT, load);
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const { data } = await api.get("/broadcasts/inbox", { params: { page: next, page_size: PAGE_SIZE } });
      setItems((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...prev, ...(data.items || []).filter((m) => !seen.has(m.id))];
      });
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
      setPage(next);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className={wrap} data-testid="messages-page">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-neutral-400 flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" /> Messages
          </div>
          <h1 className="text-2xl font-extrabold text-neutral-900">My messages</h1>
          <p className="text-xs text-neutral-500 mt-0.5" data-testid="messages-unread-count">
            {loading ? "Loading…" : unread > 0 ? `${unread} unread · ${total} in all` : `${total} message${total === 1 ? "" : "s"}, all read`}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}
                disabled={loading} className="shrink-0" data-testid="messages-refresh" aria-label="Refresh">
          <RefreshCw className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
      ) : err ? (
        <div className="py-10 text-center space-y-3">
          <div className="text-sm text-red-600">{err}</div>
          <Button type="button" variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}>Try again</Button>
        </div>
      ) : items.length === 0 ? (
        <div className="py-14 text-center text-neutral-400" data-testid="messages-empty">
          <Inbox className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
          <div className="text-sm">No messages yet.</div>
          <div className="text-xs mt-1">Messages from the office will show up here.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((m) => {
            const un = isUnread(m);
            const urgent = m.priority === "urgent";
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => navigate(`/messages/${m.id}`)}
                data-testid={`message-row-${m.id}`}
                className={`w-full text-left rounded-2xl border bg-white px-3.5 py-3 flex items-start gap-2.5 hover:bg-neutral-50 active:bg-neutral-100 transition-colors ${
                  urgent && un ? "border-red-200 border-l-4 border-l-red-500" : "border-neutral-200"
                }`}
              >
                <div className="w-2 pt-1.5 shrink-0">
                  {un && <span className="block h-2 w-2 rounded-full bg-[#E5197F]" aria-label="Unread" />}
                </div>
                <div className="flex-1 min-w-0">
                  {(urgent || m.is_active === false || m.is_expired) && (
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      {urgent && <UrgentBadge />}
                      <StateLabel m={m} />
                    </div>
                  )}
                  <div className={`text-sm break-words ${un ? "font-bold text-neutral-900" : "font-medium text-neutral-700"}`}>
                    {m.title}
                  </div>
                  <div className={`text-xs line-clamp-2 break-words mt-0.5 ${un ? "text-neutral-700" : "text-neutral-500"}`}>
                    {m.body}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-neutral-400 min-w-0">
                    <span className="truncate">{m.created_by_name || "Admin"}</span>
                    <span className="shrink-0">· {fmtWhen(m.created_at)}</span>
                    {m.attachments?.length > 0 && (
                      <span className="shrink-0 inline-flex items-center gap-0.5 ml-1">
                        <Paperclip className="h-3 w-3" />{m.attachments.length}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-neutral-300 shrink-0 mt-1" />
              </button>
            );
          })}
          {items.length < total && (
            <Button type="button" variant="outline" onClick={loadMore} disabled={loadingMore}
                    className="w-full" data-testid="messages-load-more">
              {loadingMore && <Loader2 className="animate-spin" />} Show older messages
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function MessageDetail({ id, wrap }) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [acking, setAcking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/broadcasts/${encodeURIComponent(id)}`);
      setDoc(data);
    } catch (e) {
      setDoc(null);
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const ack = async () => {
    setAcking(true);
    try {
      const { data } = await api.post(`/broadcasts/${encodeURIComponent(id)}/ack`);
      setDoc((d) => ({ ...d, my_read_at: data?.read_at || new Date().toISOString() }));
      window.dispatchEvent(new Event(BROADCASTS_CHANGED_EVENT));
      toast.success("Thank you — marked as read.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAcking(false);
    }
  };

  const backToList = (
    <button type="button" onClick={() => navigate("/messages")}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#6D2C91] hover:underline"
            data-testid="message-back-to-list">
      <ArrowLeft className="h-3.5 w-3.5" /> All messages
    </button>
  );

  if (loading) {
    return (
      <div className={wrap} data-testid="message-detail">
        {backToList}
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
      </div>
    );
  }
  if (err || !doc) {
    return (
      <div className={wrap} data-testid="message-detail">
        {backToList}
        <div className="py-10 text-center space-y-3">
          <div className="text-sm text-red-600">{err || "This message could not be loaded."}</div>
          <Button type="button" variant="outline" size="sm" onClick={load}>Try again</Button>
        </div>
      </div>
    );
  }

  const urgent = doc.priority === "urgent";
  const withdrawn = doc.is_active === false;
  const isRecipient = Object.prototype.hasOwnProperty.call(doc, "my_read_at");
  const readAt = doc.my_read_at;

  return (
    <div className={wrap} data-testid="message-detail">
      {backToList}

      <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
        <div className={`px-4 sm:px-5 pt-4 pb-3.5 text-white ${urgent ? "bg-red-600" : "tulsi-gradient"}`}>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-white/90">
            {urgent ? <AlertTriangle className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
            {urgent ? "Urgent message" : "Message"}
          </div>
          <h1 className="mt-1.5 text-xl font-extrabold leading-snug break-words" data-testid="message-detail-title">
            {doc.title}
          </h1>
          <div className="mt-1 text-xs text-white/85 break-words">
            From {doc.created_by_name || "Admin"}{doc.created_at ? ` · ${fmtWhen(doc.created_at)}` : ""}
          </div>
        </div>

        <div className="px-4 sm:px-5 py-4 space-y-4">
          {withdrawn && (
            <div className="text-xs rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-neutral-600">
              The sender has withdrawn this message. It is kept here for your reference.
            </div>
          )}
          {!withdrawn && doc.is_expired && (
            <div className="text-xs rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              This message expired on {fmtYmd(doc.expires_on)}.
            </div>
          )}
          {!withdrawn && !doc.is_expired && doc.expires_on && (
            <div className="text-[11px] text-neutral-400">Valid till {fmtYmd(doc.expires_on)}</div>
          )}

          <div className="text-[15px] leading-relaxed text-neutral-800 whitespace-pre-wrap break-words"
               data-testid="message-detail-body">
            <LinkifiedText text={doc.body} />
          </div>

          <BroadcastAttachments attachments={doc.attachments} testid="message-detail-att" />
        </div>

        {isRecipient && (
          <div className="border-t border-neutral-100 px-4 sm:px-5 py-3">
            {readAt ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="message-detail-read">
                <Check className="h-4 w-4 shrink-0" /> You read this on {fmtWhen(readAt)}
              </div>
            ) : withdrawn ? (
              <div className="text-xs text-neutral-500">No action needed.</div>
            ) : (
              <Button type="button" onClick={ack} disabled={acking} data-testid="message-detail-ack"
                      className={`w-full h-12 rounded-xl text-base font-semibold text-white ${urgent ? "bg-red-600 hover:bg-red-700" : "tulsi-gradient"}`}>
                {acking ? <Loader2 className="animate-spin" /> : <Check />}
                OK, I have read it
              </Button>
            )}
          </div>
        )}
        {!isRecipient && doc.recipient_count != null && (
          <div className="border-t border-neutral-100 px-4 sm:px-5 py-3 text-xs text-neutral-500">
            Read by {doc.read_count ?? 0} of {doc.recipient_count} people.
          </div>
        )}
      </div>
    </div>
  );
}

export default function Messages() {
  const { id } = useParams();
  const { isField } = useAuth();
  // field / distributor shells have no page padding of their own and a
  // bottom tab bar to clear; the office shell already pads its main column.
  const wrap = `${isField ? "p-4 pb-32" : ""} max-w-2xl mx-auto space-y-4`;
  return id ? <MessageDetail key={id} id={id} wrap={wrap} /> : <MessageList wrap={wrap} />;
}
